import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import Uri from 'urijs';
import { DefaultButton, DirectionalHint } from '@bingads-webui-react/fabric-control';
import { siteMapPropType, withReportsV4SiteMap } from '@bingads-webui-react/with-site-map';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { getButtonClassNames, StyledHeader, getToolbarElementsStyles } from '@bingads-webui-react/global-menu';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { withRecentReports, withSavedReports } from '@bingads-webui-campaign/reports-v4-data-source';
import { ctrLoggerFactory } from '@bingads-webui-campaign/reports-v4-telemetry';
import { withReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { genereateMenuProps } from './generate-menu-props';

export class UnthemedReportsV4DropDown extends React.PureComponent {
  renderDropdownIcon = () => null;

  renderGlyphIcon = classes => <Glyphicon className={classes.multiLevelDropDownButtonIcon} glyph="ba-Chart" />;

  renderLabel = (classes, i18n, responsiveSize) => (<StyledHeader
    key="reports-header-button"
    className={classes.multiLevelDropDownLabel}
    text={i18n.getString(_TL_('Reports'))}
    responsiveSize={responsiveSize}
  />);


  render() {
    const {
      classes,
      i18n,
      siteMap,
      responsiveSize,
      reportsV4History,
      recentReports,
      savedReports,
      instrumentation,
      serverContext,
      permissions
    } = this.props;

    const navigate = (path) => {
      const pathUri = new Uri(path);
      if (pathUri.is('absolute') === true) {
        reportsV4History.push(path);
      } else {
        const routeUri = new Uri(siteMap[0].absoluteRoute);
        if (routeUri.is('absolute') === true) {
          reportsV4History.push(siteMap[0].absoluteRoute.replace(siteMap[0].fixedRoute, path));
        } else {
          reportsV4History.push(siteMap[0].routePrefix + path);
        }
      }
    };

    const ctrLogger = instrumentation ? ctrLoggerFactory('ReportsV4GlobalMenu', instrumentation) : null;

    const menuProps =
      genereateMenuProps({
        siteMap,
        classes,
        i18n,
        recentReports,
        savedReports,
        navigate,
        serverContext,
        permissions,
        onClickRecentReportItem: ({ menuItem, report }) => {
          if (ctrLogger) {
            ctrLogger.click({
              target: 'RecentReport',
              extraData: {
                menuItem: _.pick(menuItem, 'key'),
                report: _.pick(report, 'ReportType'),
              },
            });
            ctrLogger.impressionFinish();
          }
        },
        onClickDefaultReportItem: ({ menuItem }) => {
          if (ctrLogger) {
            ctrLogger.click({
              target: 'DefaultReport',
              extraData: {
                menuItem: _.pick(menuItem, 'key'),
              },
            });
            ctrLogger.impressionFinish();
          }
        },
        onClickSavedReportItem: ({ menuItem, report }) => {
          if (ctrLogger) {
            ctrLogger.click({
              target: 'SavedReport',
              extraData: {
                menuItem: _.pick(menuItem, 'key'),
                report: _.pick(report, 'ReportType'),
              },
            });
            ctrLogger.impressionFinish();
          }
        },
        onClickCustomReportBuilderItem: ({ menuItem }) => {
          if (ctrLogger) {
            ctrLogger.click({
              target: 'CustomReportBuilder',
              extraData: {
                menuItem: _.pick(menuItem, 'key'),
              },
            });
            ctrLogger.impressionFinish();
          }
        },
      });

    menuProps.directionalHint = DirectionalHint.bottomRightEdge;
    menuProps.className = classNames([classes.menuContainer, 'global-menu-reports-v4-dropdown-menu']);
    menuProps.calloutProps = {
      preventDismissOnScroll: true,
    };

    return (
      <DefaultButton
        menuProps={menuProps}
        id="reports-v4"
        title={i18n.getString(_TL_('Reports'))}
        persistMenu
        onRenderText={() => this.renderLabel(classes, i18n, responsiveSize)}
        onRenderMenuIcon={this.renderDropdownIcon}
        onRenderIcon={() => this.renderGlyphIcon(classes)}
        onAfterMenuDismiss={() => {
          if (ctrLogger) {
            ctrLogger.impressionFinish();
          }
        }}
        onMenuClick={() => {
          if (ctrLogger) {
            ctrLogger.impressionStart();
          }
        }}
        className={classNames(getButtonClassNames(
          classes,
          ['global-menu-reports-v4-dropdown-toggle'],
          responsiveSize
        ), classes.reportsButton, 'btn', 'btn-primary')}
      />
    );
  }
}

UnthemedReportsV4DropDown.defaultProps = {
  recentReports: {},
  savedReports: {},
  instrumentation: undefined,
};

UnthemedReportsV4DropDown.propTypes = {
  recentReports: PropTypes.shape({}),
  savedReports: PropTypes.shape({}),
  serverContext: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  reportsV4History: PropTypes.shape({}).isRequired,
  responsiveSize: PropTypes.string.isRequired,
  siteMap: siteMapPropType.isRequired,
  instrumentation: PropTypes.shape({}),
};

const wrappers = [
  withInstrumentation,
  withRecentReports,
  withSavedReports,
  withI18nConsumer,
  withServerContext,
  withReportsHistory,
  _.partial(withDefaultStyles, _, getToolbarElementsStyles),
  withReportsV4SiteMap,
];

const wrappersForCCUI = [
  withI18nConsumer,
  withReportsHistory,
  _.partial(withDefaultStyles, _, getToolbarElementsStyles),
  withReportsV4SiteMap,
];

export const ReportsV4DropDown = _.compose(...wrappers)(UnthemedReportsV4DropDown);

export const CCUIReportsV4DropDown = _.compose(...wrappersForCCUI)(UnthemedReportsV4DropDown);
